import {
  type BrightnessId,
  type ConeWidthId,
  DEFAULT_NEON_TUBE_COLOR,
  DEFAULT_SOLID_COLOR,
  type DistanceId,
  type EmissionId,
  type LightTypeId,
  type SunTimeOfDayId,
} from './constants';

export type LightingPromptParams = {
  lightTypeId: LightTypeId;
  /**
   * Hex colour shared by light types whose `acceptsColor` is true (`solidColor`, `neon`).
   * Each synthesizer interprets it according to that light type's metaphor.
   */
  solidColor?: string;
  /** Only meaningful when lightTypeId === 'sun'. */
  sunTimeOfDay?: SunTimeOfDayId;
  emissionId: EmissionId;
  /** Only meaningful when emissionId === 'spotlight'. */
  coneWidth?: ConeWidthId;
  /** Signed degrees: −180..180 around the subject (0 = front). */
  azimuth: number;
  /** Signed degrees: −30..85 (0 = eye level). */
  elevation: number;
  distance: DistanceId;
  brightness: BrightnessId;
};

const FULL_CIRCLE_DEG = 360;
// Compass sectors are 45° wide, centred on the 8 cardinal/intercardinal directions.
// This is the half-sector size used to detect the front-facing range that wraps 0°/360°.
const AZIMUTH_HALF_SECTOR_DEG = 22.5;

// Compass sectors clockwise from front-right. Bearings are evenly spaced at 2× the half-sector
// size (i.e. one full sector apart). The front-facing sector that straddles 0°/360° is handled
// separately, so this table covers the seven non-front bearings.
//
// Each sector pairs a source description with a screen-space *shadow direction* — the shadow
// extends opposite the light source, expressed in camera-anchored terms ("toward the left side
// of the frame") rather than subject-relative ("the subject's left"). Subject-relative phrasing
// is what causes the model to flip light/shadow direction on portraits, since the subject's
// right is the viewer's left. The two phrases form a self-consistent anchor pair.
type AzimuthSector = { upperBound: number; sourceDescription: string; shadowDescription: string };
const AZIMUTH_SECTOR_WIDTH_DEG = AZIMUTH_HALF_SECTOR_DEG * 2;
const AZIMUTH_SECTORS_DEFS: readonly { sourceDescription: string; shadowDescription: string }[] = [
  {
    shadowDescription: 'with shadows extending toward the back-left of the frame, away from the camera',
    sourceDescription: 'from the front-right',
  },
  {
    shadowDescription: 'with shadows extending toward the left side of the frame, opposite the source',
    sourceDescription: 'from the right',
  },
  {
    shadowDescription: 'with shadows extending toward the front-left of the frame, toward the camera',
    sourceDescription: 'from the back-right',
  },
  {
    shadowDescription: 'with shadows extending toward the camera, in front of the subject',
    sourceDescription: 'from directly behind the subject',
  },
  {
    shadowDescription: 'with shadows extending toward the front-right of the frame, toward the camera',
    sourceDescription: 'from the back-left',
  },
  {
    shadowDescription: 'with shadows extending toward the right side of the frame, opposite the source',
    sourceDescription: 'from the left',
  },
  {
    shadowDescription: 'with shadows extending toward the back-right of the frame, away from the camera',
    sourceDescription: 'from the front-left',
  },
];

// Bearings step every full sector clockwise starting from the front-right wrap; the resulting
// upper bounds (exclusive) land at 67.5°, 112.5°, 157.5°, 202.5°, 247.5°, 292.5°, 337.5°.
const AZIMUTH_SECTORS: readonly AzimuthSector[] = AZIMUTH_SECTORS_DEFS.map((def, i) => ({
  ...def,
  upperBound: (i + 1) * AZIMUTH_SECTOR_WIDTH_DEG + AZIMUTH_HALF_SECTOR_DEG,
}));

// Lower bounds (exclusive). Ordered from highest to lowest so the first match wins.
// Bands span 30° each, mirroring ELEVATION_STOPS in constants.ts. The negative band covers
// the full clamped range (−30..−15) so "below" lands explicitly rather than via fallback.
const ELEVATION_BANDS: readonly { lowerBound: number; description: string }[] = [
  { description: 'positioned almost directly overhead', lowerBound: 75 },
  { description: 'high above the subject', lowerBound: 45 },
  { description: 'slightly above eye level', lowerBound: 15 },
  { description: 'at eye level', lowerBound: -15 },
  { description: 'from below the subject, angled upward', lowerBound: -90 },
];

const SPOTLIGHT_CONE_ADJECTIVES: Record<ConeWidthId, string> = {
  medium: 'medium-width',
  narrow: 'narrow, tightly focused',
  wide: 'wide-pooling',
};

// Time-of-day controls colour, temperature, and atmosphere — NOT shadow length or contrast.
// Shadow length is a function of the sun's elevation in the sky, which the user picks separately
// via the elevation slider (see `describeShadowLengthFromElevation`). Bundling shadow length here
// would let "midday" (claims short shadows) and "elevation = below" (a low-angle source) argue
// with each other in the same prompt.
const SUN_TIME_OF_DAY_PHRASES: Record<SunTimeOfDayId, string> = {
  afternoon: 'warm afternoon sunlight',
  blueHour:
    'deep cool blue twilight just after sunset / before sunrise, with low ambient skylight and saturated blue-violet sky tones',
  dusk: 'deep red-orange dusk sunlight with saturated warm tones',
  goldenHour: 'warm golden-hour sunlight with amber tones',
  midday: 'harsh midday sunlight with high contrast and a near-white colour temperature',
  morning: 'soft early-morning sunlight with cool gold tones',
};

// Shadow length is determined by the source's angle relative to the subject — low angle = long
// shadows, high angle = short shadows. Bands mirror `ELEVATION_BANDS` so the description always
// agrees with the angle the user picked.
const SHADOW_LENGTH_BANDS: readonly { lowerBound: number; description: string }[] = [
  { description: 'casting very short shadows directly beneath the subject', lowerBound: 75 },
  { description: 'casting short shadows close to the subject', lowerBound: 45 },
  { description: 'casting moderate-length shadows angled away from the light', lowerBound: 15 },
  { description: 'casting long, prominent shadows roughly parallel to the ground', lowerBound: -15 },
  { description: 'casting raking, very long shadows angled upward across the subject', lowerBound: -90 },
];

const describeShadowLengthFromElevation = (elevation: number): string =>
  SHADOW_LENGTH_BANDS.find((b) => elevation > b.lowerBound)?.description ??
  'casting raking, very long shadows angled upward across the subject';

const azimuthSectorAt = (azimuth: number): AzimuthSector | null => {
  const norm = ((azimuth % FULL_CIRCLE_DEG) + FULL_CIRCLE_DEG) % FULL_CIRCLE_DEG;

  if (norm < AZIMUTH_HALF_SECTOR_DEG || norm >= FULL_CIRCLE_DEG - AZIMUTH_HALF_SECTOR_DEG) {
    return null;
  }

  return AZIMUTH_SECTORS.find((s) => norm < s.upperBound) ?? null;
};

const describeAzimuth = (azimuth: number): string =>
  azimuthSectorAt(azimuth)?.sourceDescription ?? 'from in front of the subject';

// Shadow direction is meaningful only when the source has a horizontal component. Overhead and
// front-facing sources don't produce a clear screen-space shadow direction, so we return ''.
// Otherwise we return the camera-anchored shadow phrase paired with the source's sector.
const SHADOW_OVERHEAD_THRESHOLD_DEG = 75;

const describeShadowDirection = (azimuth: number, elevation: number): string => {
  if (elevation > SHADOW_OVERHEAD_THRESHOLD_DEG) {
    return '';
  }

  return (
    azimuthSectorAt(azimuth)?.shadowDescription ?? 'with shadows extending behind the subject, away from the camera'
  );
};

const describeElevation = (elevation: number): string => {
  return ELEVATION_BANDS.find((b) => elevation > b.lowerBound)?.description ?? 'from below, angled upward';
};

const describeDistance = (distance: DistanceId): string => {
  switch (distance) {
    case 'close':
      return 'close to the subject';
    case 'mid':
      return 'at a moderate distance';
    case 'far':
      return 'far from the subject';
  }
};

// Brightness phrasing describes *intensity only*, not shadow hardness. "Soft" in lighting
// terminology usually means soft-edged shadows, so we avoid that word here — it would fight the
// type-specific shadow hardness claim in each synthesizer's tail.
const describeBrightness = (brightness: BrightnessId): string => {
  switch (brightness) {
    case 'dim':
      return 'low-intensity';
    case 'soft':
      return 'moderate-low intensity';
    case 'balanced':
      return 'balanced, mid-range intensity';
    case 'bright':
      return 'high-intensity, brightly lit';
    case 'blazing':
      return 'extremely high-intensity';
  }
};

// Emission describes how the light is *delivered* (focused / diffuse / pooled / ambient) without
// claiming a specific shadow texture — the synthesizer tail owns the shadow story per light
// type. Mixing the two earlier produced "casting clear shadows" + tail "shadows are soft" pairs
// that the model resolved unpredictably.
const describeEmissionQuality = (emission: EmissionId, coneWidth?: ConeWidthId): string => {
  switch (emission) {
    case 'ambient':
      return 'as ambient illumination with no clear direction';
    case 'direct':
      return 'as direct, focused illumination';
    case 'softbox':
      return 'as soft, diffuse illumination';
    case 'spotlight': {
      const adjective = SPOTLIGHT_CONE_ADJECTIVES[coneWidth ?? 'medium'];

      return `as a ${adjective} spotlight that pools sharply on the subject`;
    }
  }
};

// When emission is ambient the UI greys out the position sliders to communicate "no clear
// direction" — keep the prompt aligned by omitting the directional phrases here too. Otherwise
// we'd contradict ourselves with "from the front-left ... as ambient illumination with no clear
// direction".
const directionalClause = (p: LightingPromptParams): string => {
  if (p.emissionId === 'ambient') {
    return '';
  }
  const shadowDirection = describeShadowDirection(p.azimuth, p.elevation);
  const shadowClause = shadowDirection ? `, ${shadowDirection}` : '';

  return ` ${describeAzimuth(p.azimuth)}, ${describeElevation(p.elevation)}, ${describeDistance(p.distance)}${shadowClause},`;
};

// Per-light-type synthesizers compose only the params that apply to the chosen source. This
// avoids contradictions like "ambient sunlight" or "spotlight overcast" that the previous
// generic concatenation produced.
type Synthesizer = (params: LightingPromptParams) => string;

const SYNTHESIZERS: Record<LightTypeId, Synthesizer> = {
  candle: (p) =>
    `${describeBrightness(p.brightness)} warm flickering candlelight${directionalClause(p)} ${describeEmissionQuality(p.emissionId, p.coneWidth)}. Candles are weak, low-colour-temperature point sources — falloff is rapid (areas further from the flame should be much darker) and shadows are soft, warm-orange, with subtle directional contrast.`,

  fireplace: (p) =>
    `${describeBrightness(p.brightness)} warm flickering firelight from a fireplace${directionalClause(p)} ${describeEmissionQuality(p.emissionId, p.coneWidth)}. A fireplace is a broad warm low-colour-temperature source — light has a wider spread than candlelight, with prominent warm orange/red tones, soft warm shadows, and a gentle dancing flicker quality.`,

  moon: (p) => {
    const shadowDirection = describeShadowDirection(p.azimuth, p.elevation);
    const shadowClause = shadowDirection ? `, ${shadowDirection}` : '';

    return `${describeBrightness(p.brightness)} moonlight on a clear night ${describeAzimuth(p.azimuth)} and ${describeElevation(p.elevation)}${shadowClause}. The moon is a far-away weak source — shadows are soft, low-contrast, and silver/blue cool-toned. Maintain an ambient fill consistent with night-time.`;
  },

  neon: (p) => {
    // Both `neon` and `solidColor` share the same `solidColor` field — the synthesizer chooses
    // how to interpret it. Default falls back to the canonical neon tube colour when unset.
    const tube = p.solidColor ?? DEFAULT_NEON_TUBE_COLOR;

    return `${describeBrightness(p.brightness)} neon glow with a tube colour of ${tube}${directionalClause(p)} ${describeEmissionQuality(p.emissionId, p.coneWidth)}. Neon tubes cast saturated coloured light with characteristic soft halos around bright edges — use ${tube} to tint highlights and rim-light the subject, with subtle colour bleed onto nearby surfaces.`;
  },

  overcast: (p) =>
    `${describeBrightness(p.brightness)} overcast daylight from a uniformly cloud-filtered sky. The light is omnidirectional and diffuse — no clear shadow direction, very soft falloff, neutral cool white-balance. Ignore any directional cues; treat the scene as if lit from every direction at once with consistent low-contrast soft shadows.`,

  solidColor: (p) => {
    const wash = p.solidColor ?? DEFAULT_SOLID_COLOR;

    return `a ${describeBrightness(p.brightness)} ${wash}-coloured wash${directionalClause(p)} ${describeEmissionQuality(p.emissionId, p.coneWidth)}. Treat ${wash} as a gel'd light caster — the colour dominates the scene's tint and shadows should remain consistent with the gel's colour cast, not the original lighting.`;
  },

  studioSoftbox: (p) =>
    `${describeBrightness(p.brightness)} professional studio softbox lighting${directionalClause(p)} ${describeEmissionQuality(p.emissionId, p.coneWidth)}. Softbox produces even, neutral-white illumination with gentle gradient shadows. Avoid harsh edges; preserve subject detail, especially in highlights and mid-tones.`,

  sun: (p) => {
    const sunDescription = SUN_TIME_OF_DAY_PHRASES[p.sunTimeOfDay ?? 'afternoon'];
    const shadowLength = describeShadowLengthFromElevation(p.elevation);
    const shadowDirection = describeShadowDirection(p.azimuth, p.elevation);
    const shadowDirectionClause = shadowDirection ? `, ${shadowDirection}` : '';

    return `${describeBrightness(p.brightness)} ${sunDescription} ${describeAzimuth(p.azimuth)} and ${describeElevation(p.elevation)}, ${shadowLength}${shadowDirectionClause}. The sun is a far-away point source — cast clear, directional shadows of medium-to-hard quality with warm tones tied to the time of day.`;
  },

  window: (p) =>
    `${describeBrightness(p.brightness)} soft directional daylight as if from a window${directionalClause(p)} ${describeEmissionQuality(p.emissionId, p.coneWidth)}. Window light is naturally diffused — neutral-to-cool colour temperature, gentle gradient shadows, and a falloff that gets softer as the subject sits further from the window. Treat the source as broad and soft.`,
};

export const buildLightingPrompt = (params: LightingPromptParams): string => {
  const body = SYNTHESIZERS[params.lightTypeId](params);

  return [
    'Re-light this photograph.',
    'First, analyse the existing lighting conditions in the image — identify the current light direction, hardness, colour temperature, and where shadows fall.',
    `Then re-render the scene as if it were lit by ${body}`,
    'The subjects, composition, framing and camera angle remain identical — only the lighting conditions change.',
    'Cast NEW shadows that are physically consistent with the new light source described above; completely discard the original shadow direction, length, and hardness — the new lighting setup overrides them.',
  ].join(' ');
};
